import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, type Package } from '../api/client';
import { ErrorBox, Field, Modal } from '../components/ui';
import { m3uRow, parseLineupCsv, parseM3u } from './lineupParse';
import { Select } from '../components/Select';

type ImportResult = { created: number; updated: number; logos: { channelId: string; url: string }[]; errors: { row: number; message: string }[] };

/**
 * Logos are downloaded by this browser (image hosts such as imgur refuse data-centre servers but allow
 * browsers via CORS), uploaded as media and set on the channel. What the browser cannot read goes to the server.
 */
async function importLogos(items: { channelId: string; url: string }[], progress: (done: number) => void) {
  const failed: typeof items = [];
  let done = 0;
  const queue = [...items];
  const worker = async () => {
    for (let it = queue.shift(); it; it = queue.shift()) {
      try {
        const r = await fetch(it.url, { mode: 'cors', signal: AbortSignal.timeout(10_000) });
        const blob = await r.blob();
        // imgur answers deleted images with a "no longer available" picture (redirect to /removed.png).
        if (!r.ok || r.url.endsWith('/removed.png') || !blob.type.startsWith('image/') || blob.size > 2 * 1024 * 1024) throw new Error('not an image');
        const asset = await api.upload<{ id: string }>('/assets', new File([blob], 'logo', { type: blob.type }));
        await api.patch(`/channels/${it.channelId}`, { logoAssetId: asset.id });
      } catch {
        failed.push(it);
      }
      progress(++done);
    }
  };
  await Promise.all(Array.from({ length: 6 }, worker));
  if (failed.length) await api.post('/channels/logos/fetch', { items: failed }).catch(() => undefined);
  return { stored: items.length - failed.length, handedToServer: failed.length };
}

export function ImportModal({ siteId, nextNumber, onClose, onDone }: { siteId: string; nextNumber: number; onClose: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<'m3u' | 'csv'>('m3u');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [start, setStart] = useState(nextNumber);
  const [groups, setGroups] = useState<Set<string>>(new Set());
  const packages = useQuery({ queryKey: ['packages'], queryFn: () => api.get<{ items: Package[] }>('/packages') });
  const [packageId, setPackageId] = useState<string | null>(null);
  const [logoProgress, setLogoProgress] = useState<{ done: number; total: number } | null>(null);
  const pkg = packageId ?? (packages.data?.items.find((p) => p.isDefault) ?? packages.data?.items[0])?.id ?? '';
  const entries = mode === 'm3u' ? parseM3u(text) : [];
  const groupCounts = new Map<string, number>();
  for (const e of entries) groupCounts.set(e.group, (groupCounts.get(e.group) ?? 0) + 1);
  const picked = entries.filter((e) => groups.has(e.group));
  const rows = mode === 'csv' ? parseLineupCsv(text) : picked.map((e, i) => m3uRow(e, start + i));

  const loadPlaylist = (t: string) => {
    setText(t);
    const found = parseM3u(t);
    // Small playlists: everything ticked. Big ones (hundreds of channels): the admin picks the groups.
    setGroups(new Set(found.length <= 200 ? found.map((e) => e.group) : []));
  };
  const fetchUrl = useMutation({
    mutationFn: () => api.post<{ text: string }>('/playlists/fetch', { url: url.trim() }),
    onSuccess: (r) => loadPlaylist(r.text),
  });
  const run = useMutation({
    // The API takes up to 2000 rows per call; 500 keeps each request short.
    mutationFn: async () => {
      const total: ImportResult & { logoResult?: { stored: number; handedToServer: number } } = { created: 0, updated: 0, logos: [], errors: [] };
      for (let i = 0; i < rows.length; i += 500) {
        const r = await api.post<ImportResult>(`/sites/${siteId}/lineup/import`, { rows: rows.slice(i, i + 500), packageId: pkg || undefined });
        total.created += r.created;
        total.updated += r.updated;
        total.logos.push(...(r.logos ?? []));
        total.errors.push(...r.errors.map((er) => ({ ...er, row: er.row + i })));
      }
      if (total.logos.length) {
        setLogoProgress({ done: 0, total: total.logos.length });
        total.logoResult = await importLogos(total.logos, (done) => setLogoProgress({ done, total: total.logos.length }));
      }
      return total;
    },
    onSuccess: onDone,
  });
  const toggleGroup = (g: string) =>
    setGroups((cur) => {
      const n = new Set(cur);
      if (n.has(g)) n.delete(g);
      else n.add(g);
      return n;
    });
  const switchTo = (m: 'm3u' | 'csv') => {
    setMode(m);
    setText('');
  };

  return (
    <Modal
      title="Import lineup"
      onClose={onClose}
      wide
      footer={
        <button className="btn primary" disabled={!rows.length || run.isPending} onClick={() => run.mutate()}>
          {run.isPending ? (logoProgress ? `Logos ${logoProgress.done} / ${logoProgress.total}…` : 'Importing…') : `Import ${rows.length} channels`}
        </button>
      }
    >
      <div className="tabs" style={{ marginBottom: 12 }}>
        <button className={`tab ${mode === 'm3u' ? 'active' : ''}`} onClick={() => switchTo('m3u')}>
          M3U playlist
        </button>
        <button className={`tab ${mode === 'csv' ? 'active' : ''}`} onClick={() => switchTo('csv')}>
          CSV
        </button>
      </div>
      {mode === 'm3u' ? (
        <>
          <Field label="Playlist URL" hint="An .m3u or .m3u8 link, e.g. https://raw.githubusercontent.com/Free-TV/IPTV/master/playlist.m3u8. Or upload the file below.">
            <div className="row">
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/playlist.m3u8" style={{ flex: 1 }} />
              <button className="btn" disabled={!url.trim() || fetchUrl.isPending} onClick={() => fetchUrl.mutate()}>
                {fetchUrl.isPending ? 'Loading…' : 'Load'}
              </button>
            </div>
          </Field>
          <input
            type="file"
            accept=".m3u,.m3u8,.txt"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) loadPlaylist(await f.text());
            }}
          />
          <ErrorBox error={fetchUrl.error} />
          {entries.length > 0 && (
            <>
              <div className="row" style={{ margin: '14px 0 6px' }}>
                <strong>
                  {entries.length} channels in {groupCounts.size} groups
                </strong>
                <span className="muted">Each group becomes a category. Tick the groups to import.</span>
                <span className="row" style={{ marginLeft: 'auto' }}>
                  <button className="btn" onClick={() => setGroups(new Set(groupCounts.keys()))}>
                    All
                  </button>
                  <button className="btn" onClick={() => setGroups(new Set())}>
                    None
                  </button>
                </span>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                  gap: '4px 12px',
                  maxHeight: 260,
                  overflow: 'auto',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 12,
                }}
              >
                {[...groupCounts.entries()].map(([g, n]) => (
                  <label key={g} className="row" style={{ gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={groups.has(g)} onChange={() => toggleGroup(g)} />
                    <span>{g}</span>
                    <span className="muted">{n}</span>
                  </label>
                ))}
              </div>
              <Field
                label="First channel number"
                hint={`Channels are numbered in playlist order from here. A number that already exists in this lineup is replaced, so the default starts after the last one (${nextNumber - 1}).`}
              >
                <input type="number" min={1} value={start} onChange={(e) => setStart(Math.max(1, Number(e.target.value) || 1))} style={{ width: 140 }} />
              </Field>
              {picked.length > 0 && (
                <div className="muted">
                  {start}–{start + picked.length - 1}: {picked.slice(0, 5).map((e) => e.name).join(', ')}
                  {picked.length > 5 ? ` and ${picked.length - 5} more` : ''}. Channel logos are added right after the import.
                </div>
              )}
            </>
          )}
          {text && entries.length === 0 && <div className="error">No playable channels found in this playlist.</div>}
        </>
      ) : (
        <>
          <Field
            label="Paste CSV"
            hint="number,name,category,multicast group,port — e.g. 101,Horizon News,News,239.10.1.101,5000. Existing numbers are updated; missing channels and categories are created."
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'number,name,category,host,port\n101,Horizon News,News,239.10.1.101,5000\n102,Global 24,News,239.10.1.102,5000'}
              style={{ minHeight: 220 }}
              className="mono"
            />
          </Field>
          <input
            type="file"
            accept=".csv,.txt"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) setText(await f.text());
            }}
          />
        </>
      )}
      <Field label="Add to package" hint="Rooms only see channels of their packages. Pick the package the imported channels belong to.">
        <Select value={pkg} onChange={(e) => setPackageId(e.target.value)}>
          <option value="">No package (add them later)</option>
          {packages.data?.items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.isDefault ? ' (default)' : ''}
            </option>
          ))}
        </Select>
      </Field>
      {run.data && (
        <div className="stack">
          <span className="badge ok">
            {run.data.created} added, {run.data.updated} updated
            {run.data.logoResult ? `, ${run.data.logoResult.stored} logos added` : ''}
            {run.data.logoResult?.handedToServer ? ` (${run.data.logoResult.handedToServer} more are being tried by the server)` : ''}
          </span>
          {run.data.errors.slice(0, 50).map((er) => (
            <div key={er.row} className="error">
              Row {er.row}: {er.message}
            </div>
          ))}
        </div>
      )}
      <ErrorBox error={run.error} />
    </Modal>
  );
}
